/**
 * Request Approval confirmation modal
 * Shows config stages with assigned approvers before submission
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import ReactTooltip from "react-tooltip";
import { Modal } from "../../../../components/Modal/Modal";
import {
  PrimaryButtonMediumV2,
  SecondaryButtonMediumV2,
} from "../../../../components/Buttons/Buttons";
import {
  TextXlMedium,
  TextMdRegular,
  TextMdMedium,
  TextSmRegular,
} from "../../../../components/Typography/TypographyV2";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { FlagIcon } from "../../../../components/Icons/Icons";
import { Table } from "../../../../components/Table/Table";
import { TooltipPortal } from "../../../../components/TooltipModal/TooltipPortal";
import { useProductApprovals } from "../Approvals/hooks/useProductApprovals";
import { useProductApprovers } from "../Approvals/hooks/useProductApprovers";
import type { TFunction } from "i18next";
import type { Approver } from "../../../../types/types.Workflow";
import { toTitleCase } from "../../../../util/util";
import { approvalConfirmatiomMessage } from "../Approvals/utils";
import {
  APPROVER_IDENTITY_FIELDS,
  formatApproverAttributeValue,
  getApproverExtraFields,
  getApproverStages,
} from "./requestApproval.utils";

const ModalContent = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  max-width: ${({ theme }) => theme.resolve("width_md")};
`;

const ModalTitleWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const WarningSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const WarningMessageWrapper = styled.div`
  text-align: center;
  line-height: 1.5;
`;

const ApproversSection = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const SectionTitleWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const ApproverCategoryCard = styled.div`
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryTitleWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
`;

const ApproversTableWrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const MultilineCell = styled.div`
  white-space: normal;
  word-break: break-word;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_sm")} 0;
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
`;

const ModalActions = styled.div`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  > button {
    flex: 1;
  }
`;

interface RequestApprovalModalProps {
  productId: string;
  onClose: () => void;
  onSubmit: () => void;
}

function toApproverTableData(approvers: Approver[] | undefined) {
  if (!approvers) return [];
  return approvers.map((approver) => ({
    ...approver,
    id: approver.id,
  }));
}

function buildApproverColumns(
  t: TFunction,
  nameHeader: string,
  approvers: Approver[] | undefined
) {
  const nameColumn = {
    header: t(nameHeader),
    accessorKey: "name",
    enableSorting: false,
    cell: ({ row }: { row: { original: Approver } }) => (
      <TextSmRegular>
        {row.original.firstname} {row.original.lastname}
      </TextSmRegular>
    ),
  };

  const attributeColumns = getApproverExtraFields(
    approvers,
    APPROVER_IDENTITY_FIELDS
  ).map((field) => ({
    header: t(toTitleCase(field.replace(/_/g, " "))),
    accessorKey: field,
    enableSorting: false,
    meta: { overflow: "visible" as const },
    cell: ({ row }: { row: { original: Approver } }) => (
      <MultilineCell>
        <TextSmRegular>
          {formatApproverAttributeValue(row.original[field])}
        </TextSmRegular>
      </MultilineCell>
    ),
  }));

  return [nameColumn, ...attributeColumns];
}

export function RequestApprovalModal({
  productId,
  onClose,
  onSubmit,
}: RequestApprovalModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isLoading: isApprovalsLoading, requestApproval } =
    useProductApprovals(productId);
  const {
    approvers,
    stageOrder,
    stageNames,
    isLoading: isApproversLoading,
    error: approversError,
  } = useProductApprovers(productId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading = isApprovalsLoading || isApproversLoading;

  const stages = useMemo(
    () => getApproverStages(approvers, stageOrder, stageNames),
    [approvers, stageOrder, stageNames]
  );

  const stageColumnsByKey = useMemo(() => {
    const columnsByKey: Record<
      string,
      ReturnType<typeof buildApproverColumns>
    > = {};
    for (const stage of stages) {
      columnsByKey[stage.key] = buildApproverColumns(
        t,
        "Approver",
        stage.approvers
      );
    }
    return columnsByKey;
  }, [stages, t]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await requestApproval();
      onSubmit();
    } catch (err) {
      // Error handled in hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const approversErrorMessage = useMemo(() => {
    if (approversError) {
      return toTitleCase(
        (
          approversError.response?.data?.message ?? "no approvers assigned"
        ).replace(/_/g, " ")
      );
    }
    return null;
  }, [approversError]);

  const tooltipId = "request-approval-button-tooltip";

  if (isLoading) {
    return (
      <Modal show={true} closeModal={onClose}>
        <ModalContent>
          <DelayedSpinner />
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      show={true}
      closeModal={onClose}
      overlay={true}
      modalWidth={theme.resolve("width_md")}
    >
      <ModalContent>
        <ModalTitleWrapper>
          <TextXlMedium>{t("Request Approval")}</TextXlMedium>
        </ModalTitleWrapper>
        <WarningSection>
          <FlagIcon fill="#1FB45A" width={24} height={24} />

          <WarningMessageWrapper>
            <TextMdRegular color="secondary">
              {approvalConfirmatiomMessage(t)}
            </TextMdRegular>
          </WarningMessageWrapper>
        </WarningSection>

        <ApproversSection>
          <SectionTitleWrapper>
            <TextMdMedium>{t("Approvers")}</TextMdMedium>
          </SectionTitleWrapper>

          {stages.map((stage) => (
            <ApproverCategoryCard key={stage.key}>
              <CategoryTitleWrapper>
                <TextMdMedium>{t(stage.title)}</TextMdMedium>
              </CategoryTitleWrapper>
              {stage.approvers.length > 0 ? (
                <ApproversTableWrapper>
                  <Table
                    columns={stageColumnsByKey[stage.key]}
                    data={toApproverTableData(stage.approvers)}
                    isLoading={false}
                    error={undefined}
                    enableColumnResize={false}
                    lastChildleftAlign={true}
                  />
                </ApproversTableWrapper>
              ) : (
                <EmptyState>
                  <TextSmRegular color="secondary">
                    {t("No approvers assigned")}
                  </TextSmRegular>
                </EmptyState>
              )}
            </ApproverCategoryCard>
          ))}
        </ApproversSection>

        <ModalActions>
          <SecondaryButtonMediumV2 onClick={onClose}>
            {t("Cancel")}
          </SecondaryButtonMediumV2>
          <div
            data-tip={approversErrorMessage || undefined}
            data-for={tooltipId}
            style={{ flex: 1, display: "flex" }}
          >
            <PrimaryButtonMediumV2
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!!approversErrorMessage}
              style={{ width: "100%" }}
            >
              {t("Request Approval")}
            </PrimaryButtonMediumV2>
          </div>
          {approversErrorMessage && (
            <TooltipPortal>
              <ReactTooltip
                id={tooltipId}
                place="top"
                effect="solid"
                type="dark"
                multiline={true}
              />
            </TooltipPortal>
          )}
        </ModalActions>
      </ModalContent>
    </Modal>
  );
}
